/**
 * Le palier de gamme d'une intervention (lot U4, ADR-0016) : le choisir, avec la proposition d'après les heures de la machine.
 *
 * La proposition est un BOUTON, jamais une présélection : elle vient du prochain entretien de la machine (lu seulement ici, pour
 * une machine à gamme, et seulement pour qui peut gérer l'intervention). Une machine sans compteur ou sans relevé n'a rien à
 * proposer : le choix reste libre. Le choix s'écrit par l'unique chemin de modification (jeton `revision`) ; un refus de
 * concurrence (409) relit PUIS se dit.
 */
import { useEffect, useState } from 'react';

import { ApiError, api, type BriefingIntervention, type ProchainEntretien } from '../api/client';
import { PHRASE_PALIER_REMPLACE } from '../domaine/lignes-intervention';
import {
  choixPalierPossible,
  optionPalier,
  paliersDuBriefing,
  phrasePalierChoisi,
  propositionPourIntervention,
} from '../domaine/palier-intervention';

export function PalierIntervention({
  interventionId,
  assetId,
  revision,
  plannedLevelKey,
  gamme,
  peutDeplacer,
  onChange,
  onMessage,
}: {
  interventionId: string;
  assetId: string | null;
  revision: number;
  plannedLevelKey: string | null;
  gamme: BriefingIntervention['gamme'];
  /** L'intervention est ouverte et on peut la gérer. */
  peutDeplacer: boolean;
  onChange: () => void;
  onMessage: (message: string) => void;
}) {
  const [prochain, setProchain] = useState<ProchainEntretien | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const possible = choixPalierPossible(peutDeplacer, gamme);

  useEffect(() => {
    if (!possible || assetId === null) {
      setProchain(null);
      return;
    }
    let annule = false;
    api
      .prochainEntretien(assetId)
      // Une machine sans compteur, sans relevé ou sans droit de lecture n'a rien à proposer : ce n'est pas une panne.
      .then((e) => {
        if (!annule) setProchain(e);
      })
      .catch(() => {
        if (!annule) setProchain(null);
      });
    return () => {
      annule = true;
    };
  }, [possible, assetId, plannedLevelKey]);

  if (gamme === null || paliersDuBriefing(gamme).length === 0) return null;
  const paliers = paliersDuBriefing(gamme);
  const proposition = possible ? propositionPourIntervention(prochain, paliers, plannedLevelKey) : null;

  const choisir = async (levelKey: string | null) => {
    setEnCours(true);
    setErreur(null);
    try {
      await api.choisirPalierIntervention(interventionId, revision, levelKey);
      const palier = paliers.find((p) => p.levelKey === levelKey);
      onMessage(palier ? `Palier ${palier.code} choisi : les pièces prévues suivent.` : 'Choix du palier effacé.');
      onChange();
    } catch (e) {
      // 409 : l'intervention a changé depuis l'affichage. On relit, PUIS on le dit.
      if (e instanceof ApiError && e.status === 409) onChange();
      setErreur(e instanceof ApiError || e instanceof Error ? e.message : 'Choix du palier impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="palier-intervention">
      <h3>Palier de la gamme</h3>
      <p>{phrasePalierChoisi(gamme, plannedLevelKey)}</p>
      {possible && <p className="aide">{PHRASE_PALIER_REMPLACE}</p>}
      {possible && (
        <>
          {proposition && (
            <div className="saisie-ligne">
              <button type="button" className="secondaire" disabled={enCours} onClick={() => void choisir(proposition.levelKey)}>
                Utiliser le palier {proposition.code} proposé
              </button>
              <span className="aide">{proposition.raison}</span>
            </div>
          )}
          <div className="saisie-ligne">
            <select
              aria-label="Palier de la gamme à préparer"
              disabled={enCours}
              value={plannedLevelKey ?? ''}
              onChange={(e) => void choisir(e.target.value === '' ? null : e.target.value)}
            >
              <option value="">Choisir un palier…</option>
              {paliers.map((p) => (
                <option key={p.levelKey} value={p.levelKey}>
                  {optionPalier(p)}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      {erreur && <div className="message erreur">{erreur}</div>}
    </div>
  );
}
